import { useState } from "react";
import {
  useAddApiKeyToHashKey,
  useRemoveApiKeyFromHashKey,
} from "@/features/auth/api/hash.queries";
import { toast } from "sonner";
import { extractErrorMessage } from "@/lib/errors";

/**
 * Custom hook to manage API key distribution for hash keys
 * Handles adding and removing API keys with batch operations
 */
export function useHashKeyApiKeyManagement() {
  const [selectedApiKeys, setSelectedApiKeys] = useState<
    Record<string, string[]>
  >({});
  const [keysToRemove, setKeysToRemove] = useState<Record<string, string[]>>(
    {},
  );

  const { mutate: removeApiKeyFromHashKey, isPending: isRemovingKey } =
    useRemoveApiKeyFromHashKey(false);
  const { mutate: addApiKeyToHashKey, isPending: isAddingKey } =
    useAddApiKeyToHashKey();

  const isPending = isAddingKey || isRemovingKey;

  /**
   * Toggle API key selection or removal for a specific hash key
   */
  const handleApiKeyToggle = (
    hashKeyId: string,
    apiKeyId: string,
    isAlreadyDistributed: boolean,
  ) => {
    if (isAlreadyDistributed) {
      // Toggle removal state
      setKeysToRemove((prev) => {
        const currentKeys = prev[hashKeyId] || [];
        if (currentKeys.includes(apiKeyId)) {
          return {
            ...prev,
            [hashKeyId]: currentKeys.filter((id) => id !== apiKeyId),
          };
        }
        return {
          ...prev,
          [hashKeyId]: [...currentKeys, apiKeyId],
        };
      });
    } else {
      // Toggle selection state
      setSelectedApiKeys((prev) => {
        const currentKeys = prev[hashKeyId] || [];
        if (currentKeys.includes(apiKeyId)) {
          return {
            ...prev,
            [hashKeyId]: currentKeys.filter((id) => id !== apiKeyId),
          };
        }
        return {
          ...prev,
          [hashKeyId]: [...currentKeys, apiKeyId],
        };
      });
    }
  };

  /**
   * Apply all pending changes (additions and removals) for a hash key
   */
  const handleApplyChanges = (hashKeyId: string) => {
    const selectedKeys = selectedApiKeys[hashKeyId] || [];
    const removeKeys = keysToRemove[hashKeyId] || [];
    const hasChanges = selectedKeys.length > 0 || removeKeys.length > 0;

    if (!hasChanges) {
      toast.error("No changes to apply");
      return;
    }

    let addSuccessCount = 0;
    let removeSuccessCount = 0;
    const totalOperations = selectedKeys.length + removeKeys.length;
    let completedOperations = 0;

    const checkCompletion = () => {
      completedOperations++;
      if (completedOperations === totalOperations) {
        // Clear state for this hash key
        setSelectedApiKeys((prev) => {
          const newState = { ...prev };
          delete newState[hashKeyId];
          return newState;
        });
        setKeysToRemove((prev) => {
          const newState = { ...prev };
          delete newState[hashKeyId];
          return newState;
        });

        // Show success message
        const messages = [];
        if (addSuccessCount > 0) {
          messages.push(
            `Added ${addSuccessCount} key${addSuccessCount !== 1 ? "s" : ""}`,
          );
        }
        if (removeSuccessCount > 0) {
          messages.push(
            `Removed ${removeSuccessCount} key${removeSuccessCount !== 1 ? "s" : ""}`,
          );
        }

        toast.success(messages.join(" • "));
      }
    };

    // Process additions
    selectedKeys.forEach((apiKeyId) => {
      addApiKeyToHashKey(
        { hash_key_id: hashKeyId, api_key_id: apiKeyId },
        {
          onSuccess: () => {
            addSuccessCount++;
            checkCompletion();
          },
          onError: (err: Error) => {
            const message = extractErrorMessage(err);
            toast.error(`Failed to add API key`, {
              description: message,
            });
            checkCompletion();
          },
        },
      );
    });

    // Process removals
    removeKeys.forEach((apiKeyId) => {
      removeApiKeyFromHashKey(
        { hash_key_id: hashKeyId, api_key_id: apiKeyId },
        {
          onSuccess: () => {
            removeSuccessCount++;
            checkCompletion();
          },
          onError: (err: Error) => {
            const message = extractErrorMessage(err);
            toast.error(`Failed to remove API key`, {
              description: message,
            });
            checkCompletion();
          },
        },
      );
    });
  };

  return {
    selectedApiKeys,
    keysToRemove,
    isPending,
    handleApiKeyToggle,
    handleApplyChanges,
  };
}
